import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { useCurrency } from '@/Hooks/useCurrency';
import { usePermission } from '@/Hooks/usePermission';
import { api } from '@/lib/api';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { ErrorState, LoadingState } from '@/components/ui/states';
import { TD, TH, THead, TR, Table, TableWrap } from '@/components/ui/table';
import { apiError, riskSeverityTone, type RiskRuleListItem } from './lib';
import RuleFormDialog from './RuleFormDialog';

/**
 * What counts as unusual, per rule, for this venue. A rule with no row here
 * is OFF — `GET /risk/rules` still lists it, with the shipped default shown
 * beside it, so switching one on is a one-tap decision with the consequences
 * visible first rather than a rule that silently starts naming staff the
 * moment a tenant upgrades.
 */
export default function RulesTab() {
  const { t } = useTranslation();
  const { can } = usePermission();
  const canConfigure = can('risk.configure');
  // Amounts here are tenant-wide thresholds (ADR-0017: "the thing that
  // differs is the TENANT's trade"), so the tenant's own currency, not a
  // branch's — there is no branchId on a rule to ask `useCurrency` for.
  const { format } = useCurrency(null);

  const [rows, setRows] = useState<RiskRuleListItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [editing, setEditing] = useState<RiskRuleListItem | null>(null);
  const [seeding, setSeeding] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    setError(null);
    api
      .get<RiskRuleListItem[]>('/risk/rules')
      .then(({ data }) => setRows(data))
      .catch((e) => setError(apiError(e, t('risk.rules.loadFailed'))))
      .finally(() => setLoading(false));
  }, [t]);

  useEffect(load, [load]);

  const seedDefaults = async () => {
    setSeeding(true);
    try {
      const { data } = await api.post<unknown[]>('/risk/rules/seed-defaults');
      toast.success(t('risk.rules.seeded', { count: data.length }));
      load();
    } catch (e) {
      toast.error(apiError(e, t('risk.rules.seedFailed')));
    } finally {
      setSeeding(false);
    }
  };

  if (loading) return <LoadingState label={t('common.loading')} />;
  if (error) return <ErrorState message={error} onRetry={load} />;

  return (
    <div className="space-y-3">
      {canConfigure && (
        <div className="flex justify-end">
          <Button variant="outline" size="sm" onClick={() => void seedDefaults()} disabled={seeding}>
            {t('risk.rules.seedDefaults')}
          </Button>
        </div>
      )}

      <TableWrap>
        <Table>
          <THead>
            <TR>
              <TH>{t('risk.rules.rule')}</TH>
              <TH>{t('risk.rules.status')}</TH>
              <TH>{t('risk.rules.window')}</TH>
              <TH className="text-end">{t('risk.rules.countThreshold')}</TH>
              <TH className="text-end">{t('risk.rules.amountThreshold')}</TH>
              <TH>{t('risk.rules.severity')}</TH>
              {canConfigure && <TH />}
            </TR>
          </THead>
          <tbody>
            {rows.map((rule) => (
              <TR key={rule.rule}>
                <TD className="font-medium">
                  {t(`risk.rule.${rule.rule}`)}
                  {!rule.configured && (
                    <p className="text-xs text-ink-muted">{t('risk.rules.usingDefault')}</p>
                  )}
                </TD>
                <TD>
                  <Badge tone={rule.enabled ? 'success' : 'neutral'}>
                    {t(rule.enabled ? 'risk.rules.enabled' : 'risk.rules.disabled')}
                  </Badge>
                </TD>
                <TD className="tabular-nums">{t('risk.rules.minutes', { count: rule.windowMinutes })}</TD>
                <TD className="text-end tabular-nums">{rule.countThreshold ?? '—'}</TD>
                <TD className="text-end tabular-nums">
                  {rule.amountThreshold !== null ? format(rule.amountThreshold) : '—'}
                </TD>
                <TD>
                  <Badge tone={riskSeverityTone(rule.severity)}>{t(`risk.severity.${rule.severity}`)}</Badge>
                </TD>
                {canConfigure && (
                  <TD className="text-end">
                    <Button variant="ghost" size="sm" onClick={() => setEditing(rule)}>
                      {t('common.edit')}
                    </Button>
                  </TD>
                )}
              </TR>
            ))}
          </tbody>
        </Table>
      </TableWrap>

      <Card>
        <CardHeader>
          <CardTitle className="text-sm">{t('risk.rules.footnoteTitle')}</CardTitle>
        </CardHeader>
        <CardContent className="text-xs text-ink-muted">{t('risk.rules.footnoteBody')}</CardContent>
      </Card>

      {editing && (
        <RuleFormDialog
          rule={editing}
          onClose={() => setEditing(null)}
          onSaved={() => {
            setEditing(null);
            load();
          }}
        />
      )}
    </div>
  );
}
